/**
 * «Médicos: marca de sus documentos» (administración, 20/10/2026).
 *
 * Un dato de la ficha de cada cirujano del Catálogo QX (`surgeons`,
 * `marca_documentos`): con qué marca de la casa salen sus consentimientos y
 * sus postoperatorios (cabecera, logo, datos del centro y pie). El titular
 * sale siempre con el formato de su clínica (Configuración), con su firma y
 * su sello, así que a él no se le elige. Sin marca en la ficha, la de
 * Configuración → «Documentos de los médicos»; sin ninguna, como siempre.
 *
 * Lo ve quien entra al Catálogo; lo cambia un administrador (el servidor lo
 * exige y lo apunta en el rastro). Backend: services/marca_documentos.py.
 */
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { api, mensajeError } from '../../lib/api';
import { marcasPropias, nombreDeMarca } from '../settings/MarcaDocumentosAjuste';

export default function MarcaDocumentosMedicos({ admin }) {
  const [fichas, setFichas] = useState(null);   // null = cargando
  const [marcas, setMarcas] = useState([]);
  const [error, setError] = useState('');
  const [guardando, setGuardando] = useState('');

  useEffect(() => {
    let vivo = true;
    Promise.all([api.get('/api/surgeons'), api.get('/api/marcas').catch(() => ({ data: { marcas: [] } }))])
      .then(([s, m]) => {
        if (!vivo) return;
        setFichas(Array.isArray(s?.data) ? s.data : []);
        setMarcas(marcasPropias(m?.data?.marcas));
      })
      .catch((e) => { if (vivo) { setFichas([]); setError(mensajeError(e, 'no se han podido leer')); } });
    return () => { vivo = false; };
  }, []);

  const cambiar = async (f, clave) => {
    setGuardando(f.id);
    try {
      const r = await api.put(`/api/surgeons/${f.id}`, { marca_documentos: clave });
      setFichas((prev) => prev.map((x) => (x.id === f.id
        ? { ...x, marca_documentos: r?.data?.marca_documentos ?? clave } : x)));
      toast.success(`${f.name}: ${clave ? 'marca de sus documentos cambiada' : 'sin marca propia'}`);
    } catch (e) {
      toast.error(mensajeError(e, 'No se ha podido guardar'));
    } finally {
      setGuardando('');
    }
  };

  return (
    <section className="cjo-card p-4 mt-5" data-testid="catalogo-marca-documentos">
      <h2 className="text-sm font-semibold text-[#13231E]">Médicos: marca de sus documentos</h2>
      <p className="text-xs text-muted-foreground mb-3">
        Con qué marca salen los consentimientos y los postoperatorios de cada médico del Catálogo QX: su cabecera,
        su logo, sus datos y su pie (nunca la firma ni el sello del titular). El titular, siempre con el formato de
        su clínica. Sin marca aquí, la de Configuración → «Documentos de los médicos»; sin ninguna, como siempre. Un
        consentimiento ya firmado se queda con la marca con la que se firmó.
      </p>
      {fichas === null ? <p className="text-sm text-muted-foreground">Cargando…</p>
        : error ? <p className="text-sm text-red-700" role="alert">No se ha podido leer la lista: {error}.</p>
        : fichas.length === 0 ? <p className="text-sm text-muted-foreground">No hay cirujanos dados de alta.</p>
        : (
          <div className="divide-y divide-border">
            {fichas.map((f) => {
              const valor = f.marca_documentos || '';
              const conocida = !valor || marcas.some((m) => m.clave === valor);
              return (
                <div key={f.id} className="flex flex-wrap items-center gap-3 py-2 text-sm" data-testid="catalogo-marca-medico">
                  <span className="flex-1 min-w-[12rem]">{f.name}</span>
                  {f.is_default ? (
                    <span className="text-xs text-muted-foreground" data-testid={`catalogo-marca-titular-${f.id}`}>
                      Titular: formato de su clínica (Configuración)
                    </span>
                  ) : (
                    <select value={valor} disabled={!admin || guardando === f.id}
                      onChange={(e) => cambiar(f, e.target.value)}
                      aria-label={`Marca de los documentos de ${f.name}`}
                      className="border border-border rounded-md h-8 px-2 text-sm bg-white min-w-[14rem]"
                      data-testid={`catalogo-marca-${f.id}`}>
                      <option value="">Sin marca propia (la de Configuración)</option>
                      {marcas.map((m) => <option key={m.clave} value={m.clave}>{nombreDeMarca(m)}</option>)}
                      {!conocida && <option value={valor}>{valor} (ya no está activa: no se aplica)</option>}
                    </select>
                  )}
                </div>
              );
            })}
          </div>
        )}
    </section>
  );
}
